/**
 * SimplebimLite – Upload, Verarbeitungs-Fortschritt, Ergebnis, Download-Anforderung
 * Alle DOM-IDs sind mit "sbl-" prefixed.
 *
 */

// SignalR wird als globales Browser-Script in Index.cshtml geladen:
// ~/lib/signalr/signalr.min.js
type SblHubConnection = {
    on(methodName: string, callback: (data: SblJobStatusResponse) => void): void;
    onreconnected(callback: () => void): void;
    start(): Promise<void>;
    stop(): Promise<void>;
    invoke(methodName: string, ...args: unknown[]): Promise<unknown>;
};

declare const signalR: {
    HubConnectionBuilder: new () => {
        withUrl(url: string): {
            withAutomaticReconnect(retryDelays: number[]): {
                build(): SblHubConnection;
            };
        };
    };
};

// ── Globale Typen ──────────────────────────────────────────────────────────────

/** Konfigurationsoptionen, die serverseitig über das window-Objekt bereitgestellt werden können. */
interface SblConfig {
    /** Maximale Dateigröße in MB */
    maxFileSizeMb?: number;
    /** Chunk-Größe in MB */
    chunkSizeMb?: number;
}

/** Struktur der Antwort des Upload-Initialisierungsendpunkts, inklusive Job-ID und bereits empfangener Chunks für Resume-Support. */
interface SblInitResponse {
    jobId: string;
    receivedChunks: number[];
}

/** Struktur der Status-Updates, die vom Server über SignalR gesendet werden. */
interface SblJobStatusResponse {
    jobId: string;
    status: "Uploading" | "Pending" | "Processing" | "Done" | "Failed";
    statusMessage: string;
    uploadProgress: number;
    processingProgress: number;
    originalSizeBytes: number;
    optimizedSizeBytes: number;
    latestLog: string | null;
    queuePosition?: number;
}

/** Hilfsstruktur für die Berechnung der Upload-Geschwindigkeit basierend auf den zuletzt übertragenen Chunks. */
interface SpeedSample {
    time: number;
    bytes: number;
}

/** Serverseitig übergebenes State-Objekt für die initiale UI-Konfiguration */
interface SblServerState {
    initialTile?: number;
    isProfileComplete?: boolean;
    showLoginInTile?: boolean;
    engineUnavailable?: boolean;
}

/** Struktur für die Definition der Verarbeitungsphasen, die in der UI angezeigt werden. */
interface SblProcessingPhase {
    progress: number;
    label: string;
}

/** 
 * Enumeration der Kacheln/Schritte im UI, entsprechend der Reihenfolge in Index.cshtml 
 * ! Änderungen hier müssen auch im enum in der Index.cshtml.cs übernommen werden!
 */
enum SblTile {
    Intro = 1,
    LoginRegister = 2,
    ActivationRequired = 3,
    FileSelect = 4,
    Upload = 5,
    Processing = 6,
    Result = 7,
    FinalRegistration = 8,
}

/** Erweiterung des window-Objekts ohne ES-Modul-Kontext */
interface SblWindow extends Window {
    sblConfig?: SblConfig;
    sblState?: SblServerState;
    sblProcessingPhases?: SblProcessingPhase[];
}

// ── Klasse ─────────────────────────────────────────────────────────────────────

class SimplebimLiteUploader {

    // ── Konfiguration ────────────────────────────────────────────────────────────
    private readonly MAX_FILE_SIZE_BYTES: number;
    private readonly CHUNK_SIZE: number;

    // ── Engine-Verarbeitungsschritte ──────────────────────────────────────────────
    // Primär aus Razor (window.sblProcessingPhases), Fallback für robuste Laufzeit.
    private static readonly DEFAULT_PROC_PHASES: SblProcessingPhase[] = [
        { progress: 5, label: "System- und Lizenzprüfung läuft" },
        { progress: 15, label: "Verarbeitungsumgebung wird vorbereitet" },
        { progress: 25, label: "Engine-Module werden geladen" },
        { progress: 35, label: "IFC-Datei wird eingelesen" },
        { progress: 45, label: "Modellstruktur wird analysiert" },
        { progress: 55, label: "Bauteildaten werden bereinigt" },
        { progress: 70, label: "Geometrie und Eigenschaften werden optimiert" },
        { progress: 80, label: "Optimierte IFC-Datei wird erstellt" },
        { progress: 98, label: "Verarbeitung wird abgeschlossen" },
        { progress: 100, label: "Fertig!" },
    ];

    // CLI-Logzeilen-Mapping: Reguläre Ausdrücke, die in der rohen Logzeile gesucht werden, und die zugehörige Anzeige-Message.
    private static readonly MAPPINGS: [RegExp, string][] = [
            [/validat|license|core\s*license/i, "System- und Lizenzprüfung läuft …"],
            [/startup\s*arguments|initialization\s*file/i, "Verarbeitungsumgebung wird vorbereitet …"],
            [/loading\s*modules|assembly/i, "Engine-Module werden geladen …"],
            [/\bimport\b|running\s*cubifier/i, "IFC-Datei wird eingelesen …"],
            [/ifc\s*tables|entity\s*skip\s*list/i, "Modellstruktur wird analysiert …"],
            [/applying\s*layers|layer\s*loaded/i, "Bauteildaten werden bereinigt …"],
            [/flatten|appearances|natural\s*language\s*identities/i, "Geometrie und Eigenschaften werden optimiert …"],
            [/\bexport\b/i, "Optimierte IFC-Datei wird erstellt …"],
            [/\bclose\b|no\s*errors\s*or\s*warnings/i, "Verarbeitung wird abgeschlossen …"],
            [/script\s*processed|finished|completed/i, "Fertig!"],
        ];

    // ── DOM-Referenzen ───────────────────────────────────────────────────────────
    private readonly dropZone: HTMLElement;
    private readonly dropZoneParentContainer: HTMLElement;
    private readonly fileInput: HTMLInputElement;
    private readonly fileInfo: HTMLElement;
    private readonly fileSizeEl: HTMLElement;
    private readonly timeEst: HTMLElement;
    private readonly errorMsg: HTMLElement;
    private readonly startBtn: HTMLButtonElement;

    private readonly uploadFill: HTMLElement;
    private readonly uploadPct: HTMLElement;
    private readonly uploadCount: HTMLElement;
    private readonly uploadSpeed: HTMLElement;
    private readonly uploadEta: HTMLElement;
    private readonly uploadTitle: HTMLElement;
    private readonly cancelBtn: HTMLButtonElement;

    private readonly procFill: HTMLElement;
    private readonly procPct: HTMLElement;
    private readonly procTitle: HTMLElement;

    private readonly labelBefore: HTMLElement;
    private readonly labelAfter: HTMLElement;
    private readonly labelSaved: HTMLElement;
    private readonly reductBadge: HTMLElement;
    private readonly toDownloadBtn: HTMLButtonElement;
    private donutSeg: SVGCircleElement | null = null;
    private readonly stepInfo: HTMLElement;

    private readonly finalRegisterTile: HTMLElement | null;

    // ── State ────────────────────────────────────────────────────────────────────
    private selectedFile: File | null = null;
    private currentJobId: string | null = null;
    private hubConnection: SblHubConnection | null = null;
    private cancelled = false;
    private uploadedChunkCount = 0;
    private receivedChunks: Set<number> = new Set();
    private speedSamples: SpeedSample[] = [];
    private uploadedBytes = 0;
    private totalChunks = 0;
    private activeTile = SblTile.Intro;
    private hasInitializedView = false;
    private isUploadInProgress = false;
    private displayedProcStepIdx = -1;
    private pendingProcStepIdx = -1;
    private processingStepTimer: number | null = null;
    private uploadPreparingDotsTimer: number | null = null;
    private isWaitingInQueue = false;
    private readonly MIN_PROC_STEP_DISPLAY_MS = 500;
    private readonly state = (window as unknown as SblWindow).sblState || {};
    private readonly processingPhases: SblProcessingPhase[];

    /**
     * Initialisiert den Uploader, liest Konfiguration und verdrahtet alle DOM-Referenzen sowie Events.
     */
    constructor() {
        const win = window as unknown as SblWindow;
        const cfg = win.sblConfig || {};
        this.MAX_FILE_SIZE_BYTES = (cfg.maxFileSizeMb || 2048) * 1024 * 1024;
        this.CHUNK_SIZE           = (cfg.chunkSizeMb  || 4)    * 1024 * 1024;
        this.processingPhases = this.normalizeProcessingPhases(win.sblProcessingPhases);

        this.dropZone      = this.el("sbl-drop-zone");
        this.dropZoneParentContainer = this.dropZone?.closest('.box-container') as HTMLElement | null;
        this.fileInput     = this.el("sbl-file-input")  as HTMLInputElement;
        this.fileInfo      = this.el("sbl-file-info");
        this.fileSizeEl    = this.el("sbl-file-size");
        this.timeEst       = this.el("sbl-time-estimate");
        this.errorMsg      = this.el("sbl-error-msg");
        this.startBtn      = this.el("sbl-start-btn")   as HTMLButtonElement;
        // Start-Button zunächst verstecken; wird nach Dateiauswahl sichtbar
        this.startBtn.classList.add("hidden");

        this.uploadFill    = this.el("sbl-upload-fill");
        this.uploadPct     = this.el("sbl-upload-pct");
        this.uploadCount   = this.el("sbl-upload-count");
        this.uploadSpeed   = this.el("sbl-upload-speed");
        this.uploadEta     = this.el("sbl-upload-eta");
        this.uploadTitle   = this.el("sbl-upload-title");
        this.cancelBtn     = this.el("sbl-cancel-btn")  as HTMLButtonElement;

        this.procFill      = this.el("sbl-proc-fill");
        this.procPct       = this.el("sbl-proc-pct");
        this.procTitle     = this.el("sbl-proc-title");

        this.labelBefore   = this.el("sbl-label-before");
        this.labelAfter    = this.el("sbl-label-after");
        this.labelSaved    = this.el("sbl-label-saved");
        this.reductBadge   = this.el("sbl-reduction-badge");
        this.toDownloadBtn = this.el("sbl-to-download-btn") as HTMLButtonElement;

        const seg = this.el("sbl-donut-seg");
        if (seg instanceof SVGCircleElement) this.donutSeg = seg;

        this.stepInfo      = this.el("sbl-step-info");

        this.finalRegisterTile = this.el("sbl-tile-8");

        this.bindEvents();

        const initialTile = this.isValidTile(this.state.initialTile)
            ? this.state.initialTile
            : SblTile.Intro;
        this.showTile(initialTile);
        // Nach Seiten-Reload auf Tile 8 (fehlgeschlagener FinalRegistration-POST):
        // Job-ID aus sessionStorage vorladen, damit der Download-Button nach "Zurück zum Ergebnis" funktioniert.
        if (initialTile === SblTile.FinalRegistration && !this.currentJobId) {
            try {
                const raw = sessionStorage.getItem('sbl_result');
                if (raw) {
                    const data = JSON.parse(raw) as { jobId: string };
                    if (data.jobId) {
                        this.currentJobId = data.jobId;
                        this.syncJobIdFields();
                    }
                }
            } catch { /* ignore */ }
        }
    }

    // ── Events binden ────────────────────────────────────────────────────────────

    /**
     * Registriert alle UI-Events für Upload, Navigation, Formulare.
     * @returns {void} Kein Rückgabewert.
     */
    private bindEvents(): void {
        // Datei-Auswahl per Klick / Tastatur (Accessibility)
        this.dropZone.addEventListener("click", () => this.fileInput.click());
        this.dropZone.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") 
                this.fileInput.click();
        });
        this.fileInput.addEventListener("change", () => {
            if (this.fileInput.files?.[0]) 
                this.setFile(this.fileInput.files[0]);
        });

        // Drag & Drop – einheitlich auf dem größtmöglichen Ziel registriert.
        // Da Drag-Ereignisse aus der inneren Drop-Zone nach oben bubblen, genügt
        // ein einziger Event-Target (Container mit Fallback auf Drop-Zone selbst).
        const dragTarget: HTMLElement = this.dropZoneParentContainer ?? this.dropZone;
        dragTarget.addEventListener("dragover", (e: DragEvent) => {
            e.preventDefault();
            this.dropZone.classList.add("sbl-drop-zone--over");
        });
        dragTarget.addEventListener("dragenter", (e: DragEvent) => {
            e.preventDefault();
            this.dropZone.classList.add("sbl-drop-zone--over");
        });
        dragTarget.addEventListener("dragleave", (e: DragEvent) => {
            // Over-State nur entfernen, wenn der Zeiger die Kachel wirklich verlässt (kein Flickering).
            const rect = dragTarget.getBoundingClientRect();
            if (e.clientX < rect.left || e.clientX > rect.right ||
                e.clientY < rect.top  || e.clientY > rect.bottom) 
            {
                this.dropZone.classList.remove("sbl-drop-zone--over");
            }
        });

        dragTarget.addEventListener("drop", (e: DragEvent) => {
            e.preventDefault();
            this.dropZone.classList.remove("sbl-drop-zone--over");
            this.handleDroppedFile(e.dataTransfer?.files[0]);
        });

        // Upload starten
        this.startBtn.addEventListener("click", () => {
            if (!this.selectedFile) 
                return;
            this.startBtn.disabled = true;
            this.cancelled = false;
            this.startUpload(this.selectedFile);
        });

        this.cancelBtn.addEventListener("click", () => this.cancel());

        // Download: Session-gesicherter Endpunkt; bei fehlender Anmeldung erfolgt Redirect auf Auth-Gate.
        this.toDownloadBtn.addEventListener("click", async (event: MouseEvent) => {
            event.preventDefault();

            if (!this.currentJobId) 
                return;

            const tile7 = this.el("sbl-tile-7");
            const profileComplete = tile7.getAttribute("data-profile-complete") === "true"
                || this.state.isProfileComplete === true;

            if (!profileComplete && this.finalRegisterTile) {
                if (this.state.showLoginInTile === true) {
                    // Bekannter User → direkt zur eigenständigen Login-Seite
                    const authGateUrl = `/simplebim-lite/auth-gate?jobId=${encodeURIComponent(this.currentJobId)}`;
                    const emailEl = document.getElementById("sbl-email") as HTMLInputElement | null;
                    const emailParam = emailEl?.value ? `&email=${encodeURIComponent(emailEl.value)}` : "";
                    window.location.href = `/simplebim-lite/anmelden?returnUrl=${encodeURIComponent(authGateUrl)}${emailParam}`;
                    return;
                }
                this.syncJobIdFields();
                this.showTile(SblTile.FinalRegistration);
                return;
            }

            const downloadActionForm = this.el("sbl-download-action-form") as HTMLFormElement | null;
            if (downloadActionForm) {
                this.syncJobIdFields();
                downloadActionForm.requestSubmit();
            }
        });

        // Intro-Button: weiter zur E-Mail-Angabe
        this.el("sbl-intro-next-btn")?.addEventListener("click", () => this.showTile(SblTile.LoginRegister));

        this.el("sbl-back-to-result-btn")?.addEventListener("click", () => {
            // Immer tryRestoreResultTile() aufrufen: befüllt Donut-Daten + navigiert.
            // Bei fehlendem sessionStorage-Eintrag (kein Reload) Fallback auf showTile.
            if (!this.tryRestoreResultTile()) {
                this.showTile(SblTile.Result);
            }
        });

        // Registrierungs-Formular bleibt serverseitig.
        this.el("sbl-register-form").addEventListener("submit", () => this.syncJobIdFields());

        // Seite verlassen / Tab schließen: Warnung nur bei aktiv laufendem Upload
        window.addEventListener("beforeunload", (e: BeforeUnloadEvent) => {
            if (this.isUploadInProgress) {
                e.preventDefault();
            }
        });

        return;
    }

    // ── Schritt-Navigation ───────────────────────────────────────────────────────

    /**
     * Schaltet die sichtbare Kachel um und synchronisiert Schrittanzeige sowie Marker.
     * @param {SblTile} n Nummer der anzuzeigenden Kachel.
     * @returns {void} Kein Rückgabewert.
     */
    private showTile(n: SblTile): void {
        if (this.hasInitializedView && this.activeTile !== n) {
            this.clearInlineFormMessages();
        }

        for (let i = SblTile.Intro; i <= SblTile.FinalRegistration; i++) {
            const tile = this.el(`sbl-tile-${i}`);
            tile.classList.toggle("hidden", i !== n);
        }
        document.querySelectorAll<HTMLElement>(".sbl-step").forEach(el => {
            const step = parseInt(el.dataset.step || "0", SimplebimLiteUploader.MAPPINGS.length);
            el.classList.toggle("active", step === n);
            el.classList.toggle("done",   step < n);
        });
        // Aktiven Schritt-Kreis hervorheben:
        // Intro bis Aktivierung → Kreis 1 | Dateiauswahl/Upload → Kreis 2 | Verarbeitung → Kreis 3 | Ergebnis/Abschluss → Kreis 4
        const circleIndex = n <= SblTile.ActivationRequired ? 1 : n <= SblTile.Upload ? 2 : n === SblTile.Processing ? 3 : 4;
        for (let i = 1; i <= 4; i++) {
            this.el(`sbl-step-circle-${i}`).classList.toggle("sbl--active", i === circleIndex);
        }

        const wasInitialized = this.hasInitializedView;
        this.activeTile = n;
        this.hasInitializedView = true;
        if (wasInitialized) {
            const activeTile = this.el(`sbl-tile-${n}`);
            activeTile.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }

        if(this.activeTile > 3) {
            const graphicRow = this.el(`email-only`);
            graphicRow.classList.toggle("hidden");
        }
        return;
    }

    // ── Datei-Auswahl ────────────────────────────────────────────────────────────

    /**
     * Verarbeitet eine per Drag & Drop abgelegte Datei und leitet sie an den Upload-Flow weiter.
     * @param {File | undefined | null} file Die abgelegte Datei oder undefined/null, wenn keine Datei vorlag.
     * @returns {void} Kein Rückgabewert.
     */
    private handleDroppedFile(file: File | undefined | null): void {
        if (!file) 
            return;
        
        const dt = new DataTransfer();
        dt.items.add(file);
        this.fileInput.files = dt.files;
        this.setFile(file);
    }

    /**
     * Validiert die ausgewählte Datei und aktualisiert die Upload-UI bei gültiger IFC-Datei.
     * @param {File} file Die vom Benutzer ausgewählte Datei.
     * @returns {void} Kein Rückgabewert.
     */
    private setFile(file: File): void {
        this.errorMsg.classList.add("hidden");

        // Engine nicht verfügbar (z. B. Lizenz abgelaufen) → Upload ignorieren, Hinweis steht bereits im Banner.
        if (this.state.engineUnavailable)
            return;

        if (!file.name.toLowerCase().endsWith(".ifc")) {
            this.showError("Bitte wählen Sie eine .ifc-Datei aus.");
            return;
        }
        if (file.size > this.MAX_FILE_SIZE_BYTES) {
            this.showError(`Die Datei ist zu groß (max. ${this.formatBytes(this.MAX_FILE_SIZE_BYTES)}).`);
            return;
        }

        this.selectedFile = file;
        this.fileSizeEl.textContent = this.formatBytes(file.size);
        this.timeEst.textContent    = "Geschätzte Dauer für die Verkleinerung: " + this.getTimeEstimate(file.size);

        const dropLabel = this.el("sbl-drop-label");
        dropLabel.textContent = file.name;

        this.fileInfo.classList.remove("hidden");
        this.startBtn.disabled = false;
        this.startBtn.classList.remove("hidden");

        return;
    }

    /**
     * Zeigt eine Fehlermeldung an und setzt die Upload-Eingaben in einen sicheren Zustand zurück.
     * @param {string} msg Anzuzeigender Fehlertext für den Benutzer.
     * @returns {void} Kein Rückgabewert.
     */
    private showError(msg: string, title?: string): void {
        this.errorMsg.textContent = "";

        if (title) {
            const titleEl = document.createElement("span");
            titleEl.className = "sbl-error-title";
            titleEl.textContent = title;
            this.errorMsg.appendChild(titleEl);
        }

        const textEl = document.createElement("span");
        textEl.className = "sbl-error-text";
        textEl.textContent = msg;
        this.errorMsg.appendChild(textEl);

        this.errorMsg.classList.remove("hidden");
        return;
    }

    // ── Upload ───────────────────────────────────────────────────────────────────

    /**
     * Initialisiert den Upload-Job und überträgt die Datei chunkweise inklusive Resume-Logik.
     * @param {File} file Die zu übertragende IFC-Datei.
     * @returns {Promise<void>} Kein Rückgabewert.
     */
    private async startUpload(file: File): Promise<void> {
        this.isUploadInProgress = true;
        this.showTile(SblTile.Upload);

        this.totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);

        // Initialisierung (Resume-Support)
        let initResp: Response;
        try {
            initResp = await fetch("/api/sbl/init", {
                method:  "POST",
                headers: { "Content-Type": "application/json" },
                body:    JSON.stringify({
                    fileName:    file.name,
                    fileSize:    file.size,
                    totalChunks: this.totalChunks
                })
            });
        } catch {
            this.isUploadInProgress = false;
            this.handleUploadError("Verbindungsfehler beim Starten des Uploads.");
            return;
        }

        if (!initResp.ok) {
            this.isUploadInProgress = false;
            let errorMsg = "Upload konnte nicht initialisiert werden.";
            try {
                const errBody = await initResp.json();
                if (errBody?.error) errorMsg = errBody.error;
            } 
            catch { /* Body nicht lesbar – Fallback-Text verwenden */ }
            this.handleUploadError(errorMsg);
            return;
        }

        const init: SblInitResponse = await initResp.json();
        this.currentJobId       = init.jobId;
        this.syncJobIdFields();
        this.receivedChunks     = new Set(init.receivedChunks || []);
        this.uploadedChunkCount = this.receivedChunks.size;
        this.uploadedBytes      = this.uploadedChunkCount * this.CHUNK_SIZE;
        this.speedSamples       = [];

        this.updateUploadProgress(this.uploadedChunkCount / this.totalChunks * 100);

        // Chunks hochladen
        for (let i = 0; i < this.totalChunks; i++) {
            if (this.cancelled) {
                this.isUploadInProgress = false;
                return;
            }
            if (this.receivedChunks.has(i)) continue;

            const start = i * this.CHUNK_SIZE;
            const end   = Math.min(start + this.CHUNK_SIZE, file.size);
            const blob  = file.slice(start, end);

            // Letzten noch ausstehenden Chunk erkennen: Merge-Verzögerung auf dem Server visuell abfangen
            if ((this.totalChunks - this.receivedChunks.size) === 1) {
                this.showUploadPreparingState();
            }

            const ok = await this.uploadChunk(init.jobId, i, blob);
            if (!ok) {
                this.isUploadInProgress = false;
                return;
            }

            this.receivedChunks.add(i);
            this.uploadedChunkCount++;
            this.uploadedBytes = Math.min(this.uploadedChunkCount * this.CHUNK_SIZE, file.size);

            this.speedSamples.push({ time: Date.now(), bytes: this.uploadedBytes });
            if (this.speedSamples.length > 8) this.speedSamples.shift();

            const recentSpeed = this.getRecentSpeed();
            const remaining   = file.size - this.uploadedBytes;
            const eta         = recentSpeed > 0 ? remaining / recentSpeed : null;

            this.updateUploadProgress(
                this.uploadedChunkCount / this.totalChunks * 100,
                recentSpeed,
                eta
            );
        }

        this.isUploadInProgress = false;
        if (!this.cancelled) {
            this.startProcessingPhase();
        }

        return;
    }

    /**
     * Überträgt einen einzelnen Dateichunk an das Backend.
     * @param {string} jobId Serverseitige Job-ID des Uploads.
     * @param {number} chunkIndex Laufende Nummer des Chunks.
     * @param {Blob} blob Binärdaten des Chunks.
     * @returns {Promise<boolean>} `true` bei erfolgreicher Übertragung, sonst `false`.
     */
    private async uploadChunk(jobId: string, chunkIndex: number, blob: Blob): Promise<boolean> {
        const form = new FormData();
        form.append("jobId",      jobId);
        form.append("chunkIndex", chunkIndex.toString());
        form.append("data",       blob, "chunk");

        try {
            const resp = await fetch("/api/sbl/chunk", { method: "POST", body: form });
            if (!resp.ok) {
                this.handleUploadError("Fehler beim Hochladen von Chunk " + chunkIndex);
                return false;
            }
            return true;
        } catch {
            this.handleUploadError("Verbindungsfehler beim Hochladen.");
            return false;
        }
    }

    /**
     * Aktualisiert Fortschrittsbalken, Mengenanzeige, Geschwindigkeit und Restzeit des Uploads.
     * @param {number} pct Aktueller Fortschritt in Prozent.
     * @param {number | null} [speed] Optional berechnete Upload-Geschwindigkeit in Byte pro Sekunde.
     * @param {number | null} [eta] Optional geschätzte Restdauer in Sekunden.
     * @returns {void} Kein Rückgabewert.
     */
    private updateUploadProgress(pct: number, speed?: number | null, eta?: number | null): void {
        const p = Math.min(Math.round(pct), 100);
        this.uploadFill.style.width = p + "%";
        this.uploadFill.setAttribute("aria-valuenow", p.toString());
        this.uploadPct.textContent = p + " %";
        if (this.selectedFile) {
            const transferred = Math.round(this.selectedFile.size * p / 100);
            this.uploadCount.textContent = `${this.formatBytes(transferred)} von ${this.formatBytes(this.selectedFile.size)} übertragen`;
        } 
        else {
            this.uploadCount.textContent = "0 von 0 MB übertragen";
        }
        if (speed != null) this.uploadSpeed.textContent = this.formatSpeed(speed);
        if (eta   != null) this.uploadEta.textContent   = this.formatEta(eta);
        else this.uploadEta.textContent = "Geschätzte Restdauer: –";

        return;
    }

    /**
    * Beendet den laufenden Upload-Prozess bei Fehler und springt zur Eingabekachel zurück.
    * @param {string} msg Fehlertext für die Anzeige.
    * @returns {void} Kein Rückgabewert.
    */
    private handleUploadError(msg: string): void {
        this.showError(msg);
        this.cancel();
        return;
    }

    // ── Abbrechen ───────────────────────────────────────────────────────────────

    /**
     * Bricht den aktuellen Vorgang ab, trennt SignalR und setzt alle relevanten Felder zurück.
     * @returns {void} Kein Rückgabewert.
     */
    private cancel(): void {
        this.cancelled = true;
        this.isUploadInProgress = false;
        void this.disconnectHub();
        this.clearUploadPreparingDotsTimer();
        this.showTile(SblTile.FileSelect);
        this.selectedFile      = null;
        this.currentJobId      = null;
        this.startBtn.disabled = true;
        this.startBtn.classList.add("hidden");
        this.fileInfo.classList.add("hidden");
        this.fileSizeEl.textContent = "";
        this.timeEst.textContent = "";
        const dropLabel = this.el("sbl-drop-label");
        dropLabel.textContent = "Datei ablegen oder klicken";

        return;
    }

    // ── Verarbeitungs-Phase ──────────────────────────────────────────────────────

    /**
     * Zeigt im Upload-Tile einen Übergangszustand an, während der Server den letzten Chunk
     * verarbeitet und alle Chunks zusammenführt (Merge-Delay für große Dateien).
     * @returns {void} Kein Rückgabewert.
     */
    private showUploadPreparingState(): void {
        this.uploadFill.style.width = "100%";
        this.uploadFill.setAttribute("aria-valuenow", "100");
        this.uploadPct.textContent = "100 %";
        // Animierte drei Punkte anfügen (", "., "..", "...")
        this.clearUploadPreparingDotsTimer();
        const base = "Fast fertig";
        let dots = 0;
        this.uploadTitle.textContent = base;
        this.uploadPreparingDotsTimer = window.setInterval(() => {
            dots = (dots + 1) % 4; // 0..3
            this.uploadTitle.textContent = base + ".".repeat(dots);
        }, 500);
        this.uploadCount.textContent = "Datei wird für die Verarbeitung vorbereitet";
        this.uploadSpeed.textContent = "";
        this.uploadEta.textContent = "";
        return;
    }

    /**
     * Wechselt von der Upload- in die Verarbeitungsansicht und startet die Statusanbindung.
     * @returns {void} Kein Rückgabewert.
     */
    private startProcessingPhase(): void {
        this.clearUploadPreparingDotsTimer();
        this.showTile(SblTile.Processing);
        this.procTitle.textContent = "Ihre IFC-Datei wird geprüft und optimiert";
        this.clearProcessingStepTimer();
        this.displayedProcStepIdx = -1;
        this.pendingProcStepIdx = 0;
        this.advanceProcessingStepQueue();
        void this.connectSignalR(this.currentJobId!);

        return;
    }

    /**
     * Baut die SignalR-Verbindung für Status-Updates auf und fällt bei Bedarf auf Polling zurück.
     * @param {string} jobId Job-ID, für die Statusereignisse abonniert werden.
     * @returns {Promise<void>} Kein Rückgabewert.
     */
    private async connectSignalR(jobId: string): Promise<void> {
        await this.disconnectHub();

        try {
            this.hubConnection = new signalR.HubConnectionBuilder()
                .withUrl("/hubs/simplebimlite")
                .withAutomaticReconnect([0, 1000, 3000, 5000])
                .build();

            this.hubConnection.on("JobStatusChanged", (data: SblJobStatusResponse) => {
                this.handleJobUpdate(data);
            });

            this.hubConnection.onreconnected(() => {
                void this.subscribeAndSync(jobId);
            });

            await this.hubConnection.start();
            await this.subscribeAndSync(jobId);

            return;
        } 
        catch {
            // SignalR-Verbindungsfehler
            console.error("SignalR-Verbindung konnte nicht hergestellt werden");
            this.hubConnection = null;
            return;
        }
    }

    /**
     * Abonniert den Jobkanal auf dem Hub und synchronisiert danach den zuletzt bekannten Status.
     * @param {string} jobId Job-ID des zu abonnierenden Verarbeitungsjobs.
     * @returns {Promise<void>} Kein Rückgabewert.
     */
    private async subscribeAndSync(jobId: string): Promise<void> {
        if (!this.hubConnection) return;
        const snapshot = await this.hubConnection.invoke("SubscribeToJob", jobId) as SblJobStatusResponse;
        this.handleJobUpdate(snapshot);

        return;
    }

    /**
     * Meldet den Job vom Hub ab, stoppt Polling und trennt die aktive SignalR-Verbindung.
     * @returns {Promise<void>} Kein Rückgabewert.
     */
    private async disconnectHub(): Promise<void> {
        this.clearProcessingStepTimer();

        if (!this.hubConnection) return;
        try {
            if (this.currentJobId) {
                await this.hubConnection.invoke("UnsubscribeFromJob", this.currentJobId);
            }
        } catch {
            // Verbindung kann bereits getrennt sein
        }
        try {
            await this.hubConnection.stop();
        } catch {
            // Ignorieren: wir wollen aufräumen und weitermachen
        }
        this.hubConnection = null;
        return;
    }

    /**
     * Verarbeitet eingehende Statusdaten und steuert Fortschrittsanzeige sowie Abschluss/Fehlerpfad.
     * @param {SblJobStatusResponse} data Aktuelle Statusdaten des Jobs vom Server.
     * @returns {void} Kein Rückgabewert.
     */
    private handleJobUpdate(data: SblJobStatusResponse): void {
        // Warteschlangen-Feedback: Job noch nicht in Verarbeitung
        if (data.status === "Pending") {
            this.isWaitingInQueue = true;
            this.procTitle.classList.add("sbl-proc-title--queue");
            this.procTitle.textContent = "Ihre Datei wartet in der Warteschlange...";
            const posText = (data.queuePosition != null && data.queuePosition > 0)
                ? `Position ${data.queuePosition} in der Warteschlange – Verarbeitung startet in Kürze…`
                : (data.statusMessage || "Verarbeitung startet in Kürze…");
            this.stepInfo.textContent = posText;
            return;
        }

        if (this.isWaitingInQueue) {
            this.isWaitingInQueue = false;
            this.procTitle.classList.remove("sbl-proc-title--queue");
            this.procTitle.textContent = "Ihre IFC-Datei wird geprüft und optimiert";
        }

        // Schritt x/10 aus Progress ableiten
        const phases    = this.processingPhases;
        const total     = phases.length;
        let stepIdx     = phases.findIndex(p => data.processingProgress <= p.progress);
        if (stepIdx < 0) stepIdx = total - 1;

        this.pendingProcStepIdx = Math.max(this.pendingProcStepIdx, stepIdx);
        if (this.processingStepTimer === null) {
            this.advanceProcessingStepQueue();
        }


        if (data.status === "Done") {
            this.clearProcessingStepTimer();
            void this.disconnectHub();
            // Vorletzter + Letzter Schritt nacheinander anzeigen, dann Ergebnis-Kachel
            const penult = Math.max(0, total - 2);
            const last = Math.max(0, total - 1);
            // Sofort vorletzten Schritt anzeigen
            this.renderProcessingStep(penult);
            // Kurz danach letzten Schritt anzeigen und dann zum Result wechseln
            setTimeout(() => {
                this.renderProcessingStep(last);
                setTimeout(() => this.showResultTile(data.originalSizeBytes, data.optimizedSizeBytes), 700);
            }, 900);
        } 
        else if (data.status === "Failed") {
            this.clearProcessingStepTimer();
            void this.disconnectHub();
            this.showTile(SblTile.FileSelect);

            // Server-Meldung enthält bereits die konkrete Ursache (z.B. bei OOM einen Hinweis auf zu
            // große/komplexe Dateien). Ein evtl. vorhandenes "Verarbeitung fehlgeschlagen: "-Präfix wird
            // entfernt, da es bereits als Überschrift dargestellt wird.
            let reason = data.statusMessage
                || "Die Verarbeitung konnte nicht abgeschlossen werden. Bitte kontaktieren Sie den Support (info@cycot.de).";
            const prefix = "Verarbeitung fehlgeschlagen: ";
            if (reason.startsWith(prefix)) {
                reason = reason.slice(prefix.length);
            }
            this.showError(reason, "Verarbeitung fehlgeschlagen");
        }

        return;
    }

    /**
     * Zeigt den gewünschten Verarbeitungsschritt in der UI an.
     * @param {number} stepIdx Index des anzuzeigenden Schritts basierend auf der serverseitigen Definition.
     * @returns {void} Kein Rückgabewert.
     */
    private renderProcessingStep(stepIdx: number): void {
        const phases = this.processingPhases;
        const total = phases.length;
        const safeStep = Math.max(0, Math.min(total - 1, stepIdx));
        const phase = phases[safeStep];

        this.stepInfo.textContent = `Schritt ${safeStep + 1} / ${total}: ${phase.label}`;
        this.procFill.style.width = phase.progress + "%";
        this.procFill.setAttribute("aria-valuenow", phase.progress.toString());
        this.procPct.textContent = phase.progress + " %";

        return;
    }

    /**
     * Arbeitet Schritt-Sprünge kontrolliert ab, damit Zwischenstufen sichtbar bleiben.
     * @returns {void} Kein Rückgabewert.
     */
    private advanceProcessingStepQueue(): void {
        if (this.displayedProcStepIdx < this.pendingProcStepIdx) {
            this.displayedProcStepIdx += 1;
            this.renderProcessingStep(this.displayedProcStepIdx);
        }

        if (this.displayedProcStepIdx < this.pendingProcStepIdx) {
            this.processingStepTimer = window.setTimeout(() => {
                this.processingStepTimer = null;
                this.advanceProcessingStepQueue();
            }, this.MIN_PROC_STEP_DISPLAY_MS);
            return;
        }

        this.processingStepTimer = null;
        return;
    }

    /**
     * Stoppt einen laufenden Timer für die Schritt-Nachführung.
     * @returns {void} Kein Rückgabewert.
     */
    private clearProcessingStepTimer(): void {
        if (this.processingStepTimer !== null) {
            window.clearTimeout(this.processingStepTimer);
            this.processingStepTimer = null;
        }
        return;
    }

    /**
     * Stoppt den Timer für die animierten Punkte im Upload-Preparing-Status.
     * @returns {void} Kein Rückgabewert.
     */
    private clearUploadPreparingDotsTimer(): void {
        if (this.uploadPreparingDotsTimer !== null) {
            clearInterval(this.uploadPreparingDotsTimer);
            this.uploadPreparingDotsTimer = null;
        }
        return;
    }

    /**
     * Normalisiert die serverseitig übergebenen Verarbeitungsphasen, filtert ungültige Einträge heraus, rundet Fortschrittswerte und sortiert nach Fortschritt.
     * @param phases Array von Verarbeitungsphasen, die normalisiert werden sollen.
     * @returns Normalisiertes Array von Verarbeitungsphasen.
     */
    private normalizeProcessingPhases(phases?: SblProcessingPhase[]): SblProcessingPhase[] {
        if (!Array.isArray(phases) || phases.length === 0) {
            return [...SimplebimLiteUploader.DEFAULT_PROC_PHASES];
        }

        const normalized = phases
            .filter((phase) => 
                Number.isFinite(phase?.progress) 
                && typeof phase?.label === "string" 
                && phase.label.trim().length > 0
            )
            .map((phase) => (
                    { 
                        progress: Math.max(0, Math.min(100, Math.round(phase.progress))), 
                        label: phase.label.trim() 
                    }
                )
            )
            .sort((a, b) => a.progress - b.progress);

        if (normalized.length === 0) {
            return [...SimplebimLiteUploader.DEFAULT_PROC_PHASES];
        }

        return normalized;
    }

    // ── Ergebnis-Kachel ──────────────────────────────────────────────────────────

    /**
     * Füllt die Ergebnisansicht mit Dateigrößen, Einsparung und Donut-Darstellung.
     * @param {number} origBytes Ursprüngliche Dateigröße in Byte.
     * @param {number} optBytes Optimierte Dateigröße in Byte.
     * @returns {void} Kein Rückgabewert.
     */
    private showResultTile(origBytes: number, optBytes: number): void {
        if (!origBytes || origBytes <= 0) {
            this.showTile(SblTile.LoginRegister);
            return;
        }

        const reduction = Math.max(0, 100 - optBytes / origBytes * 100);

        // Donut-Chart: Segment für Einsparung (grün); Hintergrundkreis = Originalgröße (teal)
        // Umfang bei r=46: 2 * π * 46 ≈ 289.03
        if (this.donutSeg) {
            const circumference = 2 * Math.PI * 46;
            const savedFrac     = Math.max(0, 1 - optBytes / origBytes);
            const dash          = savedFrac * circumference;
            this.donutSeg.style.strokeDasharray = `${dash.toFixed(1)} ${(circumference - dash).toFixed(1)}`;
        }

        this.labelBefore.textContent = this.formatBytes(origBytes);
        this.labelAfter.textContent  = this.formatBytes(optBytes);
        this.labelSaved.textContent  = this.formatBytes(Math.max(0, origBytes - optBytes));
        this.reductBadge.textContent = `- ${reduction.toFixed(0)} %`;

        // Ergebnisdaten in sessionStorage sichern, damit sie nach einem Seiten-Reload
        // (z. B. fehlgeschlagener FinalRegistration-POST) wiederhergestellt werden können.
        if (this.currentJobId) {
            try {
                sessionStorage.setItem('sbl_result', JSON.stringify({
                    jobId: this.currentJobId,
                    origBytes,
                    optBytes
                }));
            } catch { /* sessionStorage nicht verfügbar – ignorieren */ }
        }

        this.showTile(SblTile.Result);

        return;
    }

    /**
     * Versucht, die Ergebnis-Kachel aus sessionStorage wiederherzustellen.
     * Wird aufgerufen, wenn nach einem Seiten-Reload (z. B. fehlgeschlagener POST)
     * der JS-State verloren gegangen ist.
     * @returns {boolean} true wenn Wiederherstellung erfolgreich, false sonst.
     */
    private tryRestoreResultTile(): boolean {
        try {
            const raw = sessionStorage.getItem('sbl_result');
            if (!raw) return false;
            const data = JSON.parse(raw) as { jobId: string; origBytes: number; optBytes: number };
            if (!data.jobId || !data.origBytes) return false;

            this.currentJobId = data.jobId;
            this.syncJobIdFields();
            this.showResultTile(data.origBytes, data.optBytes);
            return true;
        } catch {
            return false;
        }
    }

    // ── Download-Anforderung / Registrierung ─────────────────────────────────────

    /**
     * Entfernt sichtbare Validierungs-/Statusmeldungen aus den Formularbereichen,
     * um bei UI-Wechseln keine veralteten Meldungen anzuzeigen.
     * @returns {void} Kein Rückgabewert.
     */
    private clearInlineFormMessages(): void {
        const messageElements = document.querySelectorAll<HTMLElement>(
            "#sbl-reg-error, #sbl-final-register-message, #sbl-auth-message");

        messageElements.forEach((element) => {
            element.textContent = "";
            element.classList.add("hidden");
            element.classList.remove("sbl-form-message--success", "sbl-form-message--error", "sbl-auth-message--success", "sbl-auth-message--error");
        });
        return;
    }

    /**
     * Schreibt die aktuelle Job-ID in das Discard-Formularfeld.
     * @returns {void} Kein Rückgabewert.
     */
    private syncJobIdFields(): void {
        if (!this.currentJobId) return;
        const discardJobId = this.el("sbl-discard-job-id") as HTMLInputElement | null;
        if (discardJobId) discardJobId.value = this.currentJobId;

        const finalJobId = this.el("sbl-final-job-id") as HTMLInputElement | null;
        if (finalJobId) finalJobId.value = this.currentJobId;

        const downloadJobId = this.el("sbl-download-job-id") as HTMLInputElement | null;
        if (downloadJobId) downloadJobId.value = this.currentJobId;

        return;
    }

    // ── Format-Hilfsmethoden ─────────────────────────────────────────────────────

    /**
     * Ermittelt eine grobe Verarbeitungsdauer auf Basis der Dateigröße.
     * @param {number} bytes Dateigröße in Byte.
     * @returns {string} Benutzerfreundliche Zeitabschätzung.
     */
    private getTimeEstimate(bytes: number): string {
        const mb = bytes / 1024 / 1024;
        if (mb <= 2)     return "ca. 10 Sekunden";
        if (mb <= 25)    return "ca. 20 Sekunden";
        if (mb <= 250)   return "ca. 2 Minuten";
        if (mb <= 500)   return "ca. 5 Minuten";
        if (mb <= 1024)  return "ca. 10 Minuten";
        if (mb <= 2048)  return "ca. 20 Minuten";
        return "ca. 20+ Minuten";
    }

    /**
     * Formatiert eine Byte-Zahl in eine lesbare Einheit (B, KB, MB).
     * @param {number} bytes Zu formatierende Byte-Anzahl.
     * @returns {string} Formatierter Größentext.
     */
    private formatBytes(bytes: number): string {
        if (bytes < 1024)           return bytes + " B";
        if (bytes < 1024 * 1024)    return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / 1024 / 1024).toFixed(1) + " MB";
    }

    /**
     * Formatiert eine Datenrate in MB/s fuer die Upload-Anzeige.
     * @param {number} bytesSec Geschwindigkeit in Byte pro Sekunde.
     * @returns {string} Formatierte Geschwindigkeitsanzeige oder leerer String.
     */
    private formatSpeed(bytesSec: number): string {
        if (!bytesSec || bytesSec <= 0) return "";
        return `(${(bytesSec / 1024 / 1024).toFixed(1)} MB/s)`;
    }

    /**
     * Formatiert eine Restdauer in Sekunden in einen lesbaren ETA-Text.
     * @param {number} seconds Restdauer in Sekunden.
     * @returns {string} Formatierte Restdauer oder leerer String bei ungültigem Wert.
     */
    private formatEta(seconds: number): string {
        if (!seconds || !isFinite(seconds) || seconds < 0) return "";
        if (seconds < 60) return `Geschätzte Restdauer: ${Math.round(seconds)}s`;
        const m = Math.floor(seconds / 60);
        const s = Math.round(seconds % 60);
        return `Geschätzte Restdauer: ${m}m ${s}s`;
    }

    // ── Interne Hilfsmethoden ────────────────────────────────────────────────────

    /**
     * Berechnet die mittlere Upload-Geschwindigkeit aus den zuletzt aufgezeichneten Messpunkten.
     * @returns {number} Datenrate in Byte pro Sekunde; `0` falls nicht berechenbar.
     */
    private getRecentSpeed(): number {
        if (this.speedSamples.length < 2) return 0;
        const first = this.speedSamples[0];
        const last  = this.speedSamples[this.speedSamples.length - 1];
        const dt    = (last.time - first.time) / 1000;
        if (dt <= 0) return 0;
        return (last.bytes - first.bytes) / dt;
    }

    /**
     * Prüft, ob ein numerischer Wert eine gültige Kachel-ID ist.
     * @param {number | undefined} value Zu prüfender Wert aus dem Serverzustand.
     * @returns {value is SblTile} `true`, wenn der Wert einer bekannten Kachel entspricht.
     */
    private isValidTile(value: number | undefined): value is SblTile {
        if (value == null) return false;
        return value >= SblTile.Intro && value <= SblTile.FinalRegistration;
    }

    /**
     * Liefert ein DOM-Element per ID und wirft bei fehlendem Element einen Fehler.
     * @param {string} id Die erwartete ID des HTML-Elements.
     * @returns {HTMLElement | null} Das gefundene HTMLElement oder null, wenn nicht gefunden.
     */
    private el(id: string): HTMLElement | null {
        const elem = document.getElementById(id);
        if (elem === null || elem === undefined) {
            return null
        }
        return elem;
    }
}

// ── Initialisierung ────────────────────────────────────────────────────────────
new SimplebimLiteUploader();